import { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { FileText, AlignLeft, FolderOpen, File, Calendar, User, Send, Server, Users, Mail } from 'lucide-react';
import Modal from '../../common/Modal';
import Button from '../../common/Button';
import { getReportById } from '../../../services/reportServiceSwitch';
import './ReportManagement.css';

/**
 * ViewReportModal Component
 *
 * Modal for viewing report details including delivery method and associations.
 */
export default function ViewReportModal({ isOpen, onClose, report }) {
  const [fullReport, setFullReport] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (report && isOpen) {
      loadFullReport();
    }
  }, [report, isOpen]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadFullReport = async () => {
    try {
      setLoading(true);
      const data = await getReportById(report.id);
      setFullReport(data);
    } catch {
      // Fall back to basic report data
      setFullReport(report);
    } finally {
      setLoading(false);
    }
  };

  if (!report) return null;

  const displayReport = fullReport || report;
  const deliveryLabel = displayReport.deliveryMethod === 'SFTP' ? 'SFTP Delivery' : displayReport.deliveryMethod === 'SFTP_PICKUP' ? 'SFTP Pickup' : 'Signed URL';

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Report Details"
      size="md"
      footer={
        <div className="modal-actions">
          <Button variant="secondary" onClick={onClose}>Close</Button>
        </div>
      }
    >
      {loading ? (
        <div className="loading-placeholder">Loading report details...</div>
      ) : (
        <div className="report-details">
          <div className="detail-section">
            <div className="detail-label"><FileText size={18} /><span>Report Name</span></div>
            <div className="detail-value">{displayReport.name}</div>
          </div>

          <div className="detail-section">
            <div className="detail-label"><AlignLeft size={18} /><span>Description</span></div>
            <div className="detail-value">{displayReport.description}</div>
          </div>

          <div className="detail-section">
            <div className="detail-label"><FolderOpen size={18} /><span>Category</span></div>
            <div className="detail-value"><span className="category-badge">{displayReport.category}</span></div>
          </div>

          {displayReport.filePath && (
            <div className="detail-section">
              <div className="detail-label"><File size={18} /><span>BOXI File Path</span></div>
              <div className="detail-value file-path">{displayReport.filePath}</div>
            </div>
          )}

          <div className="detail-section">
            <div className="detail-label"><Send size={18} /><span>Delivery Method</span></div>
            <div className="detail-value">
              <span className={`delivery-badge ${displayReport.deliveryMethod === 'SFTP' ? 'delivery-sftp' : 'delivery-url'}`}>
                {deliveryLabel}
              </span>
            </div>
          </div>

          {displayReport.deliveryMethod === 'SFTP' && displayReport.sftpSites?.length > 0 && (
            <div className="detail-section">
              <div className="detail-label"><Server size={18} /><span>SFTP Sites</span></div>
              <div className="detail-value">
                <div className="association-list">
                  {displayReport.sftpSites.map(site => (
                    <span key={site.id} className="association-tag">{site.name}</span>
                  ))}
                </div>
              </div>
            </div>
          )}

          {displayReport.deliveryMethod === 'SIGNED_URL' && displayReport.groups?.length > 0 && (
            <div className="detail-section">
              <div className="detail-label"><Users size={18} /><span>Distribution Groups</span></div>
              <div className="detail-value">
                <div className="association-list">
                  {displayReport.groups.map(group => (
                    <span key={group.id} className="association-tag">{group.name}</span>
                  ))}
                </div>
              </div>
            </div>
          )}

          {displayReport.deliveryMethod === 'SIGNED_URL' && displayReport.emails?.length > 0 && (
            <div className="detail-section">
              <div className="detail-label"><Mail size={18} /><span>Email Recipients</span></div>
              <div className="detail-value">
                <div className="association-list">
                  {displayReport.emails.map(email => (
                    <span key={email.id} className="association-tag">{email.name || email.emailAddress}</span>
                  ))}
                </div>
              </div>
            </div>
          )}

          {displayReport.deliveryMethod === 'SFTP_PICKUP' && displayReport.pickupFolders?.length > 0 && (
            <div className="detail-section">
              <div className="detail-label"><FolderOpen size={18} /><span>Pickup Folders</span></div>
              <div className="detail-value">
                <div className="association-list">
                  {displayReport.pickupFolders.map(folder => (
                    <span key={folder.id} className="association-tag">{folder.name}</span>
                  ))}
                </div>
              </div>
            </div>
          )}

          {displayReport.createdBy && (
            <div className="detail-section">
              <div className="detail-label"><User size={18} /><span>Created By</span></div>
              <div className="detail-value">{displayReport.createdBy}</div>
            </div>
          )}

          {displayReport.lastUpdated && (
            <div className="detail-section">
              <div className="detail-label"><Calendar size={18} /><span>Last Updated</span></div>
              <div className="detail-value">
                {new Date(displayReport.lastUpdated).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}
              </div>
            </div>
          )}
        </div>
      )}
    </Modal>
  );
}

ViewReportModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  report: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    name: PropTypes.string.isRequired,
    description: PropTypes.string,
    category: PropTypes.string,
    filePath: PropTypes.string,
    deliveryMethod: PropTypes.string,
    createdBy: PropTypes.string,
    lastUpdated: PropTypes.string
  })
};
